/**
 * What the board could not load, said out loud.
 *
 * Every one of these is a case where the board still renders and is WRONG in a
 * way that looks like data: no HDB key shows an empty board that reads as "no
 * open roles", an unmatched column reads as blank values, and a roster that
 * failed to load makes a staffed unit look deserted. Silence is the dangerous
 * option here, so each degradation names itself.
 *
 * Lifted out of HiringDemandTab, which sat one line under the 500-line ceiling.
 */
import type { DemandBoard } from '../../../types/taDemand';
import { Banner } from './DemandChrome';

export function DemandBanners({ board, notice }: { board: DemandBoard; notice: string | null }) {
  return (
    <>
      {!board.hdbAvailable && (
        <Banner tone="warn">
          <b>The Hiring Database is not connected</b>, so only roles entered here are shown — this
          is not &ldquo;no open roles&rdquo;. It normally reads through the proxy&rsquo;s existing{' '}
          <code className="text-[11px]">AIRTABLE_API_KEY</code>, so this means the key is unset or
          the base refused it.
          {board.hdbUnavailableReason ? ` (${board.hdbUnavailableReason})` : ''}
        </Banner>
      )}
      {board.hdbAvailable && board.hdbUnmappedFields.length > 0 && (
        <Banner tone="warn">
          <b>Some HDB columns could not be matched:</b> {board.hdbUnmappedFields.join(', ')}. Those
          values read as blank on the board — pin them with{' '}
          <code className="text-[11px]">HDB_FIELD_OVERRIDES</code>.
        </Banner>
      )}
      {!board.rosterAvailable && (
        <Banner tone="warn">
          <b>The current-team list could not be loaded</b>, so units with no open roles show as
          empty and a backfill cannot be raised from a seat.
          {board.rosterUnavailableReason ? ` (${board.rosterUnavailableReason})` : ''}
        </Banner>
      )}
      {notice && <Banner tone="info">{notice}</Banner>}
    </>
  );
}
